/**
 * @module experience/shop/account-setup
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:max-file-line-count */
/* tslint:disable:no-any */ // Need to support all data types
// Core
import { Injectable } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Validators } from '@angular/forms';
import { SelectItem } from '@support/shared/interfaces/select-item.interface';

// Service
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
// Interface
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { API2003Response } from '@support/api/proxies/responses/api-2003.interface';
import { APIError } from '@support/api/common/error.interface';
import { Address } from '@support/api/proxies/responses/api-2004.interface';
import { ProfileDetails } from '@support/api/proxies/responses/api-2800.interface';
import {
  SegmentationDetails,
  CustomerGroup,
} from '@support/api/proxies/responses/api-2021-segmentation.interface';
// Constants
import { FIELD_VALIDATIONS_CONSTANTS } from '@support/shared/services/common-api/common-api.constants';
import { BillingTypeEnum } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { ACCOUNT_SETUP_CONSTANTS } from '@experience/shop/cart/modules/account-setup/account-setup.constants';
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';
import {
  CustomerInformation,
  SegmentationRequest,
  SegmentationRequestFormValue,
} from '@experience/shop/cart/modules/account-setup/account-setup.interface';
import { TmoDropdown } from '@support/shared/widgets/interfaces/dropdown.interface';
import { CustomerDetailsResponse } from '@support/api/proxies/responses/api-2007.interface';
import { of as observableOf, throwError as observableThrowError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';

@Injectable()
export class AccountSetupService {
  constructor(
    private contentProviderService: ContentProviderService,
    private formBuilder: UntypedFormBuilder,
    private formCreationService: FormCreationService,
    private cartSupportService: CartSupportService,
    private apiGatewayService: ApiGatewayService,
    private commonApiService: CommonApiService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private objectService: ObjectService,
    private commonUtilService: CommonUtilService
  ) {}
  /**
   * Get Account Setup content from AEM call
   * @returns Observable<object>
   */
  public getAccountSetupContent(): Observable<object> {
    const billType =
      this.objectService.get(this.stateStoreUtilsService.getCustomerConfigData(), 'billingType') ||
      0;
    const billingType = BillingTypeEnum[billType];
    return this.contentProviderService
      .getContentData(CONTENT_ENDPOINT.ACCOUNT_BILLING_INFO, billingType.toLowerCase())
      .pipe(
        map((aemData: object) => {
          const contentData = {
            configurableData: null,
            authorableData: null,
          };
          contentData.configurableData = <ConfigData>(
            this.contentProviderService.getConfigurableData(aemData)
          );
          contentData.authorableData = this.contentProviderService.getAuthorableData(aemData);
          return contentData;
        })
      );
  }
  /**
   * Builds the account form group for the account setup component
   * @returns FormGroup
   */
  public buildAccountForm(
    customerInfo: CustomerDetailsResponse,
    isPostpaid: boolean
  ): UntypedFormGroup {
    const customer = {
      firstName: this.objectService.get(customerInfo, 'customer.firstName') || '',
      middleInitial: this.objectService.get(customerInfo, 'customer.middleName')
        ? this.objectService
            .get(customerInfo, 'customer.middleName')
            .substring(0, Number(ACCOUNT_SETUP_CONSTANTS.MIDDLE_NAME_MAX_LENGTH))
        : '',
      lastName: this.objectService.get(customerInfo, 'customer.lastName') || '',
    };
    const accountFormGrp = this.formBuilder.group({
      firstName: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.FIRST_NAME,
        customer.firstName,
        isPostpaid
      ),
      middleName: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.MIDDLE_INITIAL,
        customer.middleInitial,
        false
      ),
      lastName: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.LAST_NAME,
        customer.lastName,
        isPostpaid
      ),
      // @TODO the language english will be fetched from AEM
      language: this.formCreationService.getFormControl('', 'en', isPostpaid),
    });
    return accountFormGrp;
  }
  /**
   * Builds the account form group for the account setup component
   * @returns FormGroup
   */
  public buildZipAddress(customerInfo: BrowseEntry): UntypedFormGroup {
    let billingZipFormGrp;
    if (customerInfo.addresses && customerInfo.addresses.postalCode) {
      billingZipFormGrp = this.formBuilder.group({
        postalCode: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.ZIP_CODE,
          customerInfo.addresses.postalCode,
          true
        ),
      });
    } else {
      billingZipFormGrp = this.formBuilder.group({
        postalCode: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.ZIP_CODE,
          customerInfo.zip,
          true
        ),
      });
    }
    return billingZipFormGrp;
  }
  /**
   * Builds the profile login form group for the porfile login info component
   * @returns FormGroup
   */
  public buildProfileForm(customerInfo: BrowseEntry, isTestDrivePlan?: boolean): UntypedFormGroup {
    const email = !isTestDrivePlan ? this.objectService.get(customerInfo, 'email') || '' : '';
    return this.formBuilder.group({
      email: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.EMAIL_ADDRESS,
        email,
        false
      ),
      confirmEmail: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.EMAIL_ADDRESS,
        email,
        false
      ),
      pin: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.ACCOUNT_PIN,
        customerInfo.pin,
        true
      ),
      confirmPin: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.ACCOUNT_PIN,
        customerInfo.pin,
        true
      ),
      phoneNumber: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.FORMATTED_PHONE_NUMBER,
        '',
        false
      ),
    });
  }
  /**
   * API request for 2003
   * @param requestPayload API values populated for API 2003
   * @returns  Observable<any>
   */
  public postEmailValidation(email: string): Observable<API2003Response> {
    const requestPayload = {
      emailAddress: email,
    };
    return this.apiGatewayService
      .postRequest(ASSISTED_API_MAP.API_2003_postEmailValidation, requestPayload, null, null)
      .pipe(
        map((response: HttpResponse<API2003Response>) => {
          if (
            this.objectService.get(response, `body.eligibilityStatus`, '').toLocaleLowerCase() !==
            ACCOUNT_SETUP_CONSTANTS.HARDSTOP
          ) {
            const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
            customerConfigData.email = email;
            this.stateStoreUtilsService.setCustomerConfigData(customerConfigData);
          }
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * Builds the account form group for the account setup component
   * @returns FormGroup
   */
  public buildBillingAddress(
    customerInfo: BrowseEntry,
    cityNameRequired?: boolean
  ): UntypedFormGroup {
    let billingAddressFormGrp;
    const addressLine1 =
      this.objectService.get(customerInfo, 'addresses.addressLine1') || undefined;
    const addressLine2 =
      this.objectService.get(customerInfo, 'addresses.addressLine2') || undefined;
    const cityNameReq = cityNameRequired !== null ? cityNameRequired : true;
    if (
      customerInfo &&
      this.objectService.get(customerInfo, 'addresses.postalCode') &&
      this.stateStoreUtilsService.getAddFan()
    ) {
      billingAddressFormGrp = this.formBuilder.group({
        addressLine1: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.ADDRESS_LINE1,
          addressLine1,
          false
        ),
        addressLine2: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.ADDRESS_LINE2,
          addressLine2,
          false
        ),
        postalCode: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.ZIP_CODE,
          customerInfo.addresses.postalCode.split('-')[0],
          true
        ),
        cityName: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.CITY,
          customerInfo.addresses.cityName,
          cityNameReq
        ),
        stateCode: [customerInfo.addresses.stateCode, [Validators.required]],
        // @TODO the language english will be fetched from AEM
        language: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.URBANISATION,
          'en',
          false
        ),
        urbanizationCode: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.URBANISATION,
          '',
          false
        ),
      });
    } else {
      billingAddressFormGrp = this.formBuilder.group({
        addressLine1: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.ADDRESS_LINE1,
          undefined,
          false
        ),
        addressLine2: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.ADDRESS_LINE2,
          undefined,
          false
        ),
        postalCode: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.ZIP_CODE,
          customerInfo.zip.split('-')[0],
          true
        ),
        cityName: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.CITY,
          customerInfo.cityName,
          cityNameReq
        ),
        stateCode: [customerInfo.stateCode, [Validators.required]],
        // @TODO replace back after changing AEM
        language: this.formCreationService.getFormControl('', 'en', false),
        urbanizationCode: this.formCreationService.getFormControl('', '', false),
      });
    }
    return billingAddressFormGrp;
  }
  /**
   * Method to post audit record data
   * @param accountSetupform
   * @param lineId
   * @returns void
   */
  public postAuditRecordsData(
    accountSetupform: UntypedFormGroup,
    isPostpaid: boolean
  ): Observable<Object> {
    const formData = {};
    const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
    if (!isPostpaid) {
      const accountInfo = this.objectService.get(
        accountSetupform,
        `controls.accountInfoForm.value`,
        {}
      );
      customerConfigData.language = accountInfo.language;
      formData[ACCOUNT_SETUP_CONSTANTS.LANGUAGE] = customerConfigData.language;
      const profileInfo = this.objectService.get(
        accountSetupform,
        `controls.profileForm.value`,
        {}
      );
      const communicationType = accountSetupform.get('communicationMethod').value;
      this.updateCustomerInfo(
        customerConfigData,
        accountInfo,
        profileInfo,
        formData,
        communicationType
      );
    } else {
      customerConfigData.language = accountSetupform.controls.language.value;
      formData[ACCOUNT_SETUP_CONSTANTS.LANGUAGE] = customerConfigData.language;
    }
    this.stateStoreUtilsService.setCustomerConfigData(customerConfigData);
    return this.postAuditRecords(
      formData,
      this.stateStoreUtilsService.getLineIdForTileIdInFocus(),
      ACCOUNT_SETUP_CONSTANTS.LANGUAGE
    );
  }
  /**
   * Update the customer's profile, account, communication info
   * @param customerInfo : customer data cache
   * @param accountInfo : account info data
   * @param profileInfo : account info data
   * @param data : account info data
   * @param communicationType : customer prefered communicationType
   * @returns void
   */
  public updateCustomerInfo(
    customerInfo: BrowseEntry,
    accountInfo: { [key: string]: string },
    profileInfo: { [key: string]: string },
    data: { [key: string]: string },
    communicationType: string
  ): void {
    const value =
      communicationType === ACCOUNT_SETUP_CONSTANTS.PHONE_COMMUNICATION
        ? accountInfo.contactNumber
        : profileInfo.email;
    data[communicationType] = value;
    customerInfo.communicationMethod = communicationType;
    customerInfo.isAccountSetup = true;
    customerInfo.firstName = accountInfo.firstName;
    customerInfo.lastName = accountInfo.lastName;
    customerInfo.middleName = accountInfo.middleName;
    customerInfo.personalContactNumber = accountInfo.contactNumber;
    customerInfo.email = profileInfo.email;
    customerInfo.pin = profileInfo.pin;
  }
  /**
   * Get the regex for phone validation
   * @returns string
   */
  public getPhoneRegex(): string {
    return this.commonApiService.getFieldValidation(FIELD_VALIDATIONS_CONSTANTS.CHECK_NUMBER).regex;
  }
  /**
   * Requests Common API service for SelectItem[] to get States drop down
   * @returns Observable<SelectItem[]>
   */
  public getAllStates(): Observable<SelectItem[]> {
    return this.commonApiService.getStates('name', 'code');
  }
  /**
   * Method to get the addresses
   * @returns void
   */
  public getAddresses(): Observable<Address> {
    return observableOf(null);
  }

  /**
   * This method is used to get Profile Details from API 2800.
   * Gets Profile Details associated with current token.
   * @returns observable profile response
   */
  public getProfileDetails(): Observable<ProfileDetails> {
    return this.apiGatewayService
      .getRequest(ASSISTED_API_MAP.API_2800_getProfileDetails, null, null, null)
      .pipe(
        map((response: HttpResponse<ProfileDetails>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * Update or Add profileId and makes put profile API 2803 call
   * @param profileId: profileId of current token. Not required for prepaid. Sending
   * @param customerInformation
   * @return observable profile response
   */
  public updateProfileDetails(
    customerInformation: CustomerInformation,
    segmentationDetails?: SegmentationRequestFormValue
  ): Observable<ProfileDetails> {
    const requestPayload = this.getUpdateProfileRequest(customerInformation, segmentationDetails);
    // @TODO remove '1-P' after EMS make necessary changes.
    const pathParams = {
      profileId: this.objectService.get(customerInformation, 'profileId') || '1-P',
    };
    return this.apiGatewayService
      .putRequest(
        ASSISTED_API_MAP.API_2803_updateProfileDetails,
        requestPayload,
        null,
        null,
        pathParams
      )
      .pipe(
        map((response: HttpResponse<ProfileDetails>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * Fetches segmentation details
   */
  public getSegmentationDetails(): Observable<SegmentationDetails> {
    const payload = {
      type: 'segmentationReference',
      customerRole: this.stateStoreUtilsService.getRole(),
    };
    return this.apiGatewayService
      .postRequest(ASSISTED_API_MAP.API_2021_getRepDashLookupCriteria, payload, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  public buildSegmentationModalForm(isCustomer: boolean): UntypedFormGroup {
    if (isCustomer) {
      return this.formBuilder.group({
        customerGroup: this.formCreationService.getFormControl('', '', true),
        specialTreatment: this.formCreationService.getFormControl('', '', true),
        idVerification: this.formCreationService.getFormControl('', '', true),
        testIndicator: this.formCreationService.getFormControl('', '', true),
      });
    } else {
      return this.formBuilder.group({
        customerGroup: this.formCreationService.getFormControl('', '', true),
        specialTreatment: this.formCreationService.getFormControl('', '', true),
        assistanceProgram: this.formCreationService.getFormControl('', '', true),
        testIndicator: this.formCreationService.getFormControl('', '', true),
      });
    }
  }
  /**
   * It will build tmo-dropdown values for customer group
   * @param manufacturer Drop Down values.
   * @returns TmoDropdown as an array
   */
  public buildCustomerGroupDropDown(customerGroup: Object[]): TmoDropdown[] {
    const customerGroupDropDown = this.commonUtilService.createDropDownData(
      customerGroup,
      ACCOUNT_SETUP_CONSTANTS.DISPLAY_VALUE,
      ''
    );
    return customerGroupDropDown;
  }

  /**
   * It will build tmo-dropdown values for customerGroup sub dropdown
   * @param manufacturer Drop Down values.
   * @returns TmoDropdown as an array
   */
  public buildCustomerGroupSubDropDown(customerGroupProperties: CustomerGroup[]): TmoDropdown[] {
    const customerGroupSubDropDown = this.commonUtilService.createDropDownData(
      customerGroupProperties,
      ACCOUNT_SETUP_CONSTANTS.DISPLAY_VALUE,
      ''
    );
    return customerGroupSubDropDown;
  }

  /**
   * gets the request for update profile API call
   */
  private getUpdateProfileRequest(
    customerInformation: CustomerInformation,
    segmentationDetails?: SegmentationRequestFormValue
  ): Object {
    if (!this.objectService.get(customerInformation, 'isPostpaid')) {
      const accountInfoForm = this.objectService.get(
        customerInformation,
        'accountSetupForm.controls.accountInfoForm.value',
        {}
      );
      const email: string = this.objectService.get(
        this.stateStoreUtilsService.getCustomerConfigData(),
        'email'
      );
      const pin = this.objectService.get(
        customerInformation.accountSetupForm,
        'controls.profileForm.value.pin'
      );
      const phoneNumber = this.commonUtilService.formatMsisdnTenDigits(
        this.objectService.get(
          customerInformation,
          'accountSetupForm.controls.profileForm.controls.phoneNumber.value'
        )
      );
      return {
        firstName: accountInfoForm.firstName,
        middleName: accountInfoForm.middleName,
        lastName: accountInfoForm.lastName,
        language: this.objectService.get(
          customerInformation,
          'accountSetupForm.controls.billingAddressForm.controls.language.value',
          ACCOUNT_SETUP_CONSTANTS.ENGLISH
        ),
        emailId: email,
        phoneNo: phoneNumber,
        pin: pin,
        addressId: customerInformation.addressId,
        segmentation: this.getSegmentationRequest(segmentationDetails),
      };
    }
    return {};
  }

  /**
   * Method to post edit records for Audit purpose
   * @param itemControl : Form control that need to be post for audit
   * @param lineId : line id
   * @returns void
   */
  private postAuditRecords(
    itemControl: { [key: string]: string },
    lineId: string,
    contextType?: string
  ): Observable<Object> {
    return this.cartSupportService.postAuditRecords(itemControl, lineId, contextType).pipe(
      map((response: Object) => {
        return response;
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Method to get Segmenation Request
   * @param segmentationDetails : SegmentationRequestFormValue
   * @returns void
   */
  private getSegmentationRequest(
    segmentationDetails?: SegmentationRequestFormValue
  ): SegmentationRequest {
    let segmentationRequest: SegmentationRequest = segmentationDetails ? {} : undefined;
    if (segmentationDetails) {
      segmentationRequest = {
        customer: {
          group: this.objectService.get(segmentationDetails, 'customer.group'),
          test: this.objectService.get(segmentationDetails, 'customer.test.attributeValue'),
          idVerification: this.objectService.get(
            segmentationDetails,
            'customer.idVerification.attributeValue'
          ),
          assistance: this.objectService.get(
            segmentationDetails,
            'customer.assistance.attributeValue'
          ),
        },
        account: {
          group: this.objectService.get(segmentationDetails, 'account.group'),
          test: this.objectService.get(segmentationDetails, 'account.test.attributeValue'),
          idVerification: this.objectService.get(
            segmentationDetails,
            'account.idVerification.attributeValue'
          ),
          assistance: this.objectService.get(
            segmentationDetails,
            'account.assistance.attributeValue'
          ),
        },
      };
    }
    return segmentationRequest;
  }

  public isInvalidPin(formGroup: UntypedFormGroup): boolean {
    return !formGroup.controls.pin.valid && formGroup.controls.pin.touched;
  }
}
